/**
 * Pure helpers for detecting the native mobile-app context.
 *
 * Two independent questions, two mechanisms:
 *  1. "Is this the app ORIGIN?"  → hostname === APP_HOST. Safe to use anywhere
 *     (edge, server, client) because the host is part of the CDN cache key.
 *  2. "Is the native BRIDGE present?" → client-only, via window.InceptionAINative
 *     (see components/runtime/native-app-context.tsx). NEVER decide this
 *     server-side from the User-Agent: document responses are CDN-cached with
 *     s-maxage=28800 and no Vary: User-Agent, so UA-forked HTML would poison
 *     the cache for real browsers.
 *
 * Deliberately no next/headers import and no RSC helper — reading headers()
 * in a layout would make the whole subtree dynamic and kill ISR site-wide.
 */

export const APP_HOST = process.env.NEXT_PUBLIC_APP_HOST ?? 'app.inceptionia.com';

/** True when the given hostname is the dedicated native-app origin. */
export function isAppHostname(host: string | null | undefined): boolean {
  if (!host) return false;
  return (host.split(':')[0] ?? '').toLowerCase() === APP_HOST;
}

export type ShellPlatform = 'ios' | 'android';

export type ParsedShellUa = {
  platform: ShellPlatform | null;
  /** Shell semver, e.g. "1.0.3" — gates bridge features via min-shell-version. */
  version: string | null;
};

/**
 * The shell appends `InceptionAIApp/<version> (<platform>)` to the WebView UA.
 * ⚠ The token must never contain bot/crawl/spider/headless/preview — see
 * GENERIC_BOT_REGEX in shared/src/tracking/bot-patterns.ts, which would
 * classify all app traffic as bots and drop it from every dashboard.
 */
const SHELL_UA_PATTERN = /InceptionAIApp\/(\d+(?:\.\d+){0,2})\s*\((ios|android)\)/i;

export function parseShellUa(ua: string | null | undefined): ParsedShellUa {
  if (!ua) return { platform: null, version: null };
  const match = ua.match(SHELL_UA_PATTERN);
  if (!match) return { platform: null, version: null };
  return {
    platform: match[2].toLowerCase() as ShellPlatform,
    version: match[1],
  };
}
